/** Icons + plain-language labels for the ten initial constraint
 *  kinds (issue #30). Each entry exposes:
 *
 *  - `label`: short user-facing name (layman's term)
 *  - `engineering`: precise math/CAD term, used as a tooltip subtitle
 *  - `description`: one-line explanation
 *  - `svg`: the 24×24 glyph, read from `src/icons/constraint-*.svg` through
 *           the shared {@link stripIconSvg} (#216) and painted with
 *           `currentColor` so it picks up the surrounding text colour
 *
 *  The glyphs stay runtime constants rather than `<!-- svg:… -->` markers
 *  because a constraint chip is built on demand for whichever constraints a
 *  line happens to carry — there is no markup to hang a marker on.
 *
 *  The wording is paraphrased from the issue so the user-visible
 *  copy doesn't repeat the original verbatim. */

import type { ConstraintKind } from "../model/Project";
import coincidentRaw from "./constraint-coincident.svg?raw";
import collinearRaw from "./constraint-collinear.svg?raw";
import concentricRaw from "./constraint-concentric.svg?raw";
import equalRaw from "./constraint-equal.svg?raw";
import midpointRaw from "./constraint-midpoint.svg?raw";
import onLineRaw from "./constraint-on-line.svg?raw";
import parallelRaw from "./constraint-parallel.svg?raw";
import perpendicularRaw from "./constraint-perpendicular.svg?raw";
import symmetryRaw from "./constraint-symmetry.svg?raw";
import tangentRaw from "./constraint-tangent.svg?raw";
import { stripIconSvg } from "./stripIconSvg";

export interface ConstraintIcon {
  label: string;
  engineering: string;
  description: string;
  svg: string;
}

const ICONS: Record<ConstraintKind, ConstraintIcon> = {
  coincident: {
    label: "Snap",
    engineering: "Coincident",
    description: "Two points are pinned together at the same spot.",
    svg: stripIconSvg(coincidentRaw),
  },
  "on-line": {
    label: "Point on line",
    engineering: "Coincident (point ↔ curve)",
    description: "A point sits exactly on a line or curve.",
    svg: stripIconSvg(onLineRaw),
  },
  perpendicular: {
    label: "Right angle",
    engineering: "Perpendicular",
    description: "Two lines meet at exactly 90°.",
    svg: stripIconSvg(perpendicularRaw),
  },
  tangent: {
    label: "Touching",
    engineering: "Tangent",
    description: "A curve meets a line or another curve at exactly one point.",
    svg: stripIconSvg(tangentRaw),
  },
  equal: {
    label: "Same size",
    engineering: "Equal",
    description: "Two lines share a length, or two arcs share a radius.",
    svg: stripIconSvg(equalRaw),
  },
  parallel: {
    label: "Parallel",
    engineering: "Parallel",
    description: "Two or more lines stay parallel to each other.",
    svg: stripIconSvg(parallelRaw),
  },
  midpoint: {
    label: "Midpoint",
    engineering: "Midpoint",
    description: "A point sits exactly halfway along a line or arc.",
    svg: stripIconSvg(midpointRaw),
  },
  concentric: {
    label: "Same centre",
    engineering: "Concentric",
    description: "Two or more shapes share a single centre point.",
    svg: stripIconSvg(concentricRaw),
  },
  collinear: {
    label: "On same line",
    engineering: "Collinear",
    description: "Lines lie along a single infinite straight line.",
    svg: stripIconSvg(collinearRaw),
  },
  symmetry: {
    label: "Mirror",
    engineering: "Symmetry",
    description: "Two shapes are mirrored across a chosen line of symmetry.",
    svg: stripIconSvg(symmetryRaw),
  },
};

export function constraintIcon(kind: ConstraintKind): ConstraintIcon {
  return ICONS[kind];
}

/** Stable display order for the constraint palette / picker UI. */
export const CONSTRAINT_KINDS: ConstraintKind[] = [
  "coincident",
  "on-line",
  "perpendicular",
  "tangent",
  "equal",
  "parallel",
  "midpoint",
  "concentric",
  "collinear",
  "symmetry",
];
